<template>
  <div class="two-factor-settings">
    <Toggle toggle-trigger-classes="p-0">
      <template #title>
        Two-Factor Authentication
        <Badge
          class="status-badge"
          :intent="currentApp.settings.is2FAEnabled ? 'brand' : 'default'"
        >
          {{ currentApp.settings.is2FAEnabled ? 'Enabled' : 'Disabled' }}
        </Badge>
      </template>
      <template #content>
        <div class="mt-6">
          <div class="two-factor-content">
            <div class="description-section">
              <p
                class="description-text"
                style="white-space: pre-line;"
              >
                {{ descriptionText }}
              </p>
            </div>

            <!-- Modern Status & Control Section -->
            <div class="status-control-section">
              <!-- Warning Banner for Disabled Users -->
              <div
                v-if="currentApp.settings.is2FAEnabled && disabled2faCount > 0"
                class="warning-section"
              >
                <div class="info-banner">
                  <strong>Exception{{ disabled2faCount !== 1 ? 's' : '' }}:&nbsp;</strong>
                  {{ disabled2faCount }} individual user{{ disabled2faCount !== 1 ? 's have' : ' has' }}
                  Two-Factor Authentication disabled
                </div>
              </div>

              <!-- Button Row -->
              <div class="button-row">
                <!-- Starter Plan Button -->
                <a
                  v-if="isStarterPlan"
                  :href="billingPlansUrl"
                  target="_blank"
                  class="button small bg-gradient-primary rounded-lg
                    border-0 p-3 text-base text-white leading-4 font-medium"
                >
                  <FontAwesome
                    name="diamond"
                    class="mr-2"
                  />
                  View Plans and Upgrade
                </a>
                <!-- Regular 2FA Buttons -->
                <template v-else>
                  <a
                    v-if="currentApp.settings.is2FAEnabled"
                    class="button small delete bg-white rounded-lg
                      border border-destructive p-3 text-base text-destructive leading-4 font-medium"
                    :class="{ 'opacity-50 cursor-not-allowed': isUpdating }"
                    @click="!isUpdating && toggleTwoFactor()"
                  >
                    {{ isUpdating ? 'Updating...' : 'Disable for All Users' }}
                  </a>
                  <a
                    v-else
                    class="button small bg-gradient-primary rounded-lg
                      border-0 p-3 text-base text-white leading-4 font-medium"
                    :class="{ 'opacity-50 cursor-not-allowed': isUpdating }"
                    @click="!isUpdating && toggleTwoFactor()"
                  >
                    {{ isUpdating ? 'Enabling...' : 'Enable for All Users' }}
                  </a>
                </template>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Toggle>

    <!-- Two-Factor Authentication Modal -->
    <TwoFactorAuthenticationModal
      v-if="showModal"
      :user="modalUser"
      @close="closeModal"
      @update-2fa-status="handleModalConfirm"
    />
  </div>
</template>

<script>
import Toggle from '@/components/ui/Toggle';
import Badge from '@/components/ui/Badge.vue';
import FontAwesome from '@/components/ui/FontAwesome.vue';
import RequestUtils from '@/components/util/RequestUtils';
import TwoFactorAuthenticationModal from './TwoFactorAuthenticationModal.vue';
import { trackSecondaryEvent } from '@/lib/rudderstack-helper';

export default {
  name: 'TwoFactorSettings',
  components: {
    Toggle,
    Badge,
    FontAwesome,
    TwoFactorAuthenticationModal,
  },
  mixins: [
    RequestUtils,
  ],
  props: {
    app: {
      type: Object,
      required: true,
    },
  },
  emits: ['update:app'],
  data() {
    return {
      disabled2faCount: 0,
      loading: false,
      isUpdating: false,
      localApp: null,
      showModal: false,
    };
  },
  computed: {
    currentApp() {
      return this.localApp || this.app;
    },
    modalUser() {
      // Create a user object that represents the app-wide 2FA setting
      return {
        twoFactorEnabled: this.currentApp.settings.is2FAEnabled,
        name: 'All Users',
        email: 'app-wide-setting',
      };
    },
    isStarterPlan() {
      const planId = this.currentApp?.account?.product_plan?.id;
      return planId && planId.startsWith('starter_');
    },
    billingPlansUrl() {
      return `${process.env.VUE_APP_DASHBOARD_URL}/settings/billing/plans`;
    },
    descriptionText() {
      if (this.isStarterPlan) {
        return 'Upgrade to Pro or above to enable 2FA.\n\nEnabling 2FA requires Live App users to verify their identity using email, password, and a 2FA code to access protected pages.';
      }
      return 'Require all Live App users to verify their identity using email, password, and a two-factor authentication code to access protected pages. This setting applies automatically to all users, but can be managed for individual accounts in the Users table.';
    },
  },
  watch: {
    app: {
      handler(newApp) {
        this.localApp = { ...newApp };
      },
      immediate: true,
      deep: true,
    },
    'currentApp.settings.is2FAEnabled': {
      handler(newVal) {
        if (newVal) {
          this.fetchDisabled2faCount();
        }
      },
    },
  },
  mounted() {
    this.fetchDisabled2faCount();
  },
  methods: {
    async toggleTwoFactor() {
      // Instead of saving immediately, show the modal
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    async handleModalConfirm(newStatus) {
      this.isUpdating = true;
      const originalValue = this.localApp.settings.is2FAEnabled;

      try {
        // Update the setting in local copy
        this.localApp.settings.is2FAEnabled = newStatus;

        // If enabling 2FA, set optional to false if undefined
        if (this.localApp.settings.is2FAEnabled) {
          await this.fetchDisabled2faCount();
        }

        await this.saveSettings();

        // Emit event to parent to update app data
        this.$emit('update:app', this.localApp);

        // Close modal on success
        this.closeModal();
      } catch (error) {
        // Revert the change if save failed
        this.localApp.settings.is2FAEnabled = originalValue;
        this.$store.dispatch('toasts/addToast', {
          type: 'error',
          message: 'Error updating Two-Factor Authentication settings',
        });
      } finally {
        this.isUpdating = false;
      }
    },

    refreshAppSettings() {
      // Refetch the app data similar to Users.vue pattern
      this.localApp = { ...this.$store.getters.app.raw() };
      // Emit updated app to parent
      this.$emit('update:app', this.localApp);
    },

    async fetchDisabled2faCount() {
      if (!this.localApp.settings.is2FAEnabled) {
        this.disabled2faCount = 0;
        return;
      }

      // Find the all_users object
      const allUsersObject = this.$store.getters.objects.find((object) => object.profile_key === 'all_users');

      if (!allUsersObject) {
        this.disabled2faCount = 0;
        return;
      }

      // Find the 2FA boolean field - use user property to match server logic
      const twoFactorField = allUsersObject.fields.find(
        (field) => field?.attributes?.user === true && field?.attributes?.type === 'boolean',
      );

      if (!twoFactorField) {
        this.disabled2faCount = 0;
        return;
      }

      // Create filter to find users with 2FA disabled (field is not true)
      const filters = [
        {
          field: twoFactorField.key,
          operator: 'is not',
          value: true,
        },
      ];

      this.commitRequest({
        request: () => window.Knack.Api.getRecords(
          allUsersObject.key,
          filters,
        ),
        onSuccess: (response) => {
          // Use total_records to get the actual count of users with 2FA disabled
          this.disabled2faCount = response.total_records || 0;
        },
        onError: () => {
          this.disabled2faCount = 0;
          // No toast - fail silently for statistics
        },
      });
    },

    async saveSettings() {
      const shouldEnable2FA = this.localApp.settings.is2FAEnabled;

      this.commitRequest({
        request: () => this.$store.getters.app.update({
          settings: {
            is2FAEnabled: this.localApp.settings.is2FAEnabled,
          },
        }),
        onSuccess: () => {
          if (shouldEnable2FA) {
            trackSecondaryEvent('2fa_enabled', {}, 10);
          }

          // Refetch app settings to update the UI immediately
          this.refreshAppSettings();

          const message = shouldEnable2FA
            ? 'Two-Factor Authentication has been enabled for all users'
            : 'Two-Factor Authentication has been disabled for all users';

          this.$store.dispatch('toasts/addToast', {
            type: 'success',
            message,
          });
        },
        onError: () => {
          const message = shouldEnable2FA
            ? 'Error enabling Two-Factor Authentication for all users'
            : 'Error disabling Two-Factor Authentication for all users';

          this.$store.dispatch('toasts/addToast', {
            type: 'error',
            message,
          });
        },
      });
    },
  },
};
</script>

<style scoped>
/* Modern Two Factor Settings Styles */
.two-factor-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.status-badge {
  margin-left: 1rem;
}

.description-section {
  margin-bottom: 8px;
}

.description-text {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}

.status-control-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.status-row {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.status-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.button-row {
  margin-top: 5px;
}

.warning-section {
  margin-top: 8px;
}

.info-banner {
  display: flex;
  width: 100%;
  padding: 12px 16px;
  align-items: center;
  border-radius: 8px;
  border: 1px solid #E5E7EB;
  background-color: #F9FAFB;
  color: #374151;
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .status-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .button-row {
    align-self: stretch;
  }
}
</style>
